<template>
  <div
    id="view-license"
    class="container-fluid">
    <div class="row">
      <div class="col-4">
        <b-form
          id="license-form"
          name="licenseForm"
          class="pt-1 crud-form"
          @submit.prevent="submitLicense">
          <div class="row mt-1">
            <div class="col">
              <h3 class="m-0 text-secondary d-flex align-items-center justify-content-between">
                <span>Edit License</span>
                <b-badge
                  pill
                  variant="danger"
                  v-if="isExpired(license.ExpirationDate)">
                  Expired
                </b-badge>
              </h3>
              <hr class="mt-2 mb-0">
            </div>
          </div>

          <div class="row">
            <!-- Customer -->
            <div class="col">
              <label for="license-customer">
                Customer:
              </label>
              <b-form-input
                id="license-customer"
                type="text"
                name="licenseCustomer"
                size="sm"
                :value="`${license.Customer.LastName}, ${license.Customer.FirstName}`"
                readonly
              />
            </div>
            <!-- / Customer -->
          </div>

          <div class="row mt-3">
            <div class="col">
              <!-- Access Code -->
              <label for="license-refresh-interval">
                Access Code:
              </label>

              <b-form-input
                id="license-access-code"
                class="text-uppercase"
                type="text"
                size="sm"
                name="licenseAccessCode"
                :value="license.AccessCode"
                v-select-on-click
                readonly
              />
              <!-- / Access Code -->
            </div>
          </div>

          <div class="row mt-3">
            <div class="col">
              <div class="row">
                <!-- Device Limit -->
                <div class="col-6">
                  <label for="license-device-limit">
                    Device Limit:
                    <small class="text-danger">required</small>
                  </label>

                  <input
                    id="license-device-limit"
                    type="number"
                    class="form-control form-control-sm"
                    name="licenseDeviceLimit"
                    placeholder="Enter a device limit"
                    v-model="license.DeviceLimit"
                  />
                </div>
                <!-- / Device Limit -->

                <!-- Plot Limit -->
                <div class="col-6">
                  <label for="license-plot-limit">
                    Plot Limit:
                    <small class="text-danger">required</small>
                  </label>

                  <input
                    id="license-plot-limit"
                    type="number"
                    class="form-control form-control-sm"
                    name="licensePlotLimit"
                    placeholder="Enter a plot limit"
                    v-model="license.PlotLimit"
                  />
                </div>
                <!-- / Plot Limit -->
              </div>
            </div>
          </div>

          <div class="row mt-3">
            <!-- Refresh Interval -->
            <div class="col-6">
              <label for="license-refresh-interval">
                Refresh Interval (days):
                <small class="text-danger">required</small>
              </label>

              <input
                id="license-refresh-interval"
                type="number"
                class="form-control form-control-sm"
                name="licenseRefreshInterval"
                placeholder="Enter a refresh interval"
                v-model="license.RefreshInterval"
              />
            </div>
            <!-- / Refresh Interval -->
          </div>

          <div class="row mt-3">
            <!-- Features -->
            <div class="col">
              <label for="license-features">
                Features:
              </label>

              <b-form-checkbox-group
                v-model="license.FeatureIDs"
                value-field="ID"
                text-field="Name"
                :options="features"
              />
            </div>
            <!-- / Features -->
          </div>

          <div class="row mt-3">
            <div class="col">
              <div class="row">
                <!-- Starting Date -->
                <div class="col-6">
                  <label for="license-starting-date">
                    Starting Date:
                    <small class="text-danger">required</small>
                  </label>

                  <b-form-input
                    id="license-starting-date"
                    type="date"
                    size="sm"
                    name="licenseStartingDate"
                    v-model="license.StartingDate"
                  />
                </div>
                <!-- / Starting Date -->

                <!-- Expiration Date -->
                <div class="col-6 pl-0">
                  <label for="license-expiration-date">
                    Expiration Date:
                    <small class="text-danger">required</small>
                  </label>

                  <b-form-input
                    id="license-expiration-date"
                    type="date"
                    size="sm"
                    name="licenseExpirationDate"
                    v-model="license.ExpirationDate"
                  />
                </div>
                <!-- / Expiration Date -->
              </div>
            </div>
          </div>

          <div class="row mt-4 crud-actions justify-content-end">
            <!-- Submit -->
            <div class="col-auto">
              <b-button
                type="submit"
                size="sm"
                variant="success">
                Save
                <font-awesome-icon icon="save" size="sm"></font-awesome-icon>
              </b-button>
            </div>
            <!-- / Submit -->

            <!-- Revoke -->
            <div class="col-auto pl-0">
              <b-button
                type="button"
                size="sm"
                variant="danger"
                :disabled="isExpired(license.ExpirationDate)"
                @click="revokeLicense">
                Revoke
                <font-awesome-icon icon="times-circle" size="sm"></font-awesome-icon>
              </b-button>
            </div>
            <!-- / Revoke -->

            <!-- Clear / Reset -->
            <div class="col-auto pl-0">
              <b-button
                type="button"
                size="sm"
                variant="primary"
                @click="resetLicense">
                Reset
                <font-awesome-icon icon="sync" size="sm"></font-awesome-icon>
              </b-button>
            </div>
            <!-- / Clear / Reset -->
          </div>
        </b-form>
      </div>

      <div class="col-8">
        <div class="row mt-1 pt-1">
          <div class="col d-flex align-items-end">
            <h3 class="m-0 text-secondary">Activations ({{license.Usage.length}})</h3>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col">
            <b-table
              striped
              :items="usageWithDeviceInfo"
              :fields="usageFields"
              :show-empty="true"
              empty-text="There are no activations to show.">
              <template
                slot="Approved"
                slot-scope="data">
                <b-form-checkbox
                  v-model="data.item.Approved"
                  @input="approveUsage(data.item)">
                </b-form-checkbox>
              </template>
            </b-table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import DataSvc from '../services/data';
import mixins from '../services/mixins';
import { GlobalLoaderMixin } from '../services/event-bus';
import DirtyFormMixin from '../services/dirty-form';

export default {
  name: 'view-license',
  mixins: [
    GlobalLoaderMixin,
    mixins,
    DirtyFormMixin,
  ],
  data() {
    return {
      license: {
        FeatureIDs: [],
        Usage: [],
        Customer: {
          FirstName: '',
          LastName: '',
        },
      },
      licenseCopy: {},
      features: [],
      usageFields: [
        {
          label: 'Fingerprint',
          key: 'DeviceFingerprint',
        },
        {
          label: 'OS',
          key: 'DeviceOS',
        },
        {
          label: 'CPU',
          key: 'DeviceCPU',
        },
        {
          label: 'RAM',
          key: 'DeviceRAM',
        },
        {
          label: 'Disk',
          key: 'DeviceDisk',
        },
        {
          label: 'Activated',
          key: 'CreatedDateTime',
          tdClass: 'text-center',
        },
        {
          label: 'Approved',
          key: 'Approved',
          tdClass: 'text-center',
        },
      ],
    };
  },
  computed: {
    isDirty() {
      const licenseJSON = JSON.stringify(this.license);
      const licenseCopyJSON = JSON.stringify(this.licenseCopy);
      return !_.isEqual(licenseJSON, licenseCopyJSON);
    },
    usageWithDeviceInfo() {

      return this.license.Usage.map((u) => {
        const parsedInfo = JSON.parse(u.DeviceInfo);
        if (parsedInfo === null) {
          return {};
        }
        const newUsage = Object.assign({}, u);
        const cpu = parsedInfo.cpu_info[0];
        const os = parsedInfo.os_info[0];
        const kbytesInGB = 1024 * 1024;
        const ramSizeGB = Math.round(os.TotalVisibleMemorySize / kbytesInGB);
        const disk = parsedInfo.disk_info[0];

        const bytesInGB = 1073741824;
        const diskSizeGB = Math.round(parseInt(disk.Size) / bytesInGB);

        // We don't need to see the whole SHA hash, just the first 10 chars should be fine
        newUsage.DeviceFingerprint = newUsage.DeviceThumbprint.substr(0, 10);
        newUsage.DeviceCPU = `${cpu.Name} ${cpu.NumberOfCores} cores, ${cpu.NumberOfLogicalProcessors} logical cores`;
        newUsage.DeviceOS = `${os.Caption} ${os.OSArchitecture}`;
        newUsage.DeviceRAM = `${ramSizeGB}GB`;
        newUsage.DeviceDisk = `${diskSizeGB}GB`;
        return newUsage;
      });
    },
  },
  methods: {
    submitLicense() {
      this.showGlobalLoader('Saving license...');

      return DataSvc.updateLicense(this.license)
        .then(this.processLicenseResponse)
        .finally(() => {
          this.hideGlobalLoader();
        });
    },
    revokeLicense() {
      const confirmMsg = 'Are you sure you want to revoke this license?';

      const revokeConfirmed = () => {
        this.showGlobalLoader('Revoking license...');

        return DataSvc.revokeLicenses([this.license])
          .then((licenses) => {
            this.processLicenseResponse(licenses[0]);
          })
          .finally(() => {
            this.hideGlobalLoader();
          });
      };

      return this.showConfirm(confirmMsg).then(revokeConfirmed);
    },
    resetLicense() {
      this.license = _.cloneDeep(this.licenseCopy);
    },
    approveUsage(usage) {
      return DataSvc.approveUsage(usage);
    },
    getLicense(licenseID) {
      return DataSvc.getLicense(licenseID).then(this.processLicenseResponse);
    },
    getFeatures() {
      return DataSvc.getFeatures()
        .then((features) => {
          this.features = features;
        });
    },
    processLicenseResponse(license) {
      [license.StartingDate] = license.StartingDate.split('T');
      [license.ExpirationDate] = license.ExpirationDate.split('T');

      if (!license.Usage) {
        license.Usage = [];
      }

      license.Usage.forEach((u) => {
        u.CreatedDateTime = moment(u.CreatedDateTime).format('M/D/YYYY [at] h:mm:ss a');
      });

      this.license = license;
      this.licenseCopy = _.cloneDeep(this.license);
    },
  },
  created() {
    this.showGlobalLoader('Loading license...');

    const licensePromise = this.getLicense(this.$route.params.ID)
      .catch(() => {
        this.$router.push({ name: 'list-licenses' });
      });

    const featurePromise = this.getFeatures();

    Promise.all([
      licensePromise,
      featurePromise,
    ]).finally(() => {
      this.hideGlobalLoader();
    });
  },
};
</script>

<style lang="scss">
</style>
